import React, { useState, useEffect } from 'react';
import imageCompression from 'browser-image-compression';
import { getAllTag } from '../api/tagApi';
import { getAllRegions } from '../api/regionApi';
import { uploadSpace, updateSpace, updateSpaceData } from '../api/spaceApi';

interface SpaceFormProps {
  initialData?: {
    id?: string;
    title?: string;
    description?: string;
    location?: string;
    pricePerDay?: number;
    maxDurationDays?: number;
    main_image?: string | File;
    tags?: number[];
    regionId?: number;
    status?: string;
  };
  onSubmitSuccess?: () => void;
}

const SpaceForm: React.FC<SpaceFormProps> = ({ initialData, onSubmitSuccess }) => {
  const [formState, setFormState] = useState({
    spaceName: initialData?.title || '',
    description: initialData?.description || '',
    location: initialData?.location || '',
    pricePerDay: initialData?.pricePerDay || 3000,
    maxDurationDays: initialData?.maxDurationDays || 30,
    main_image: initialData?.main_image || null,
    tags: initialData?.tags || [],
    regionId: initialData?.regionId || null,
    status: initialData?.status
  });

  const [availableTags, setAvailableTags] = useState<{ id: number; name: string }[]>([]);
  const [availableRegions, setAvailableRegions] = useState<{ id: number; name: string }[]>([]);

  useEffect(() => {
    const fetchTags = async () => {
      const res = await getAllTag();
      const data = await res.json();
      setAvailableTags(data.body.tags);
    };
    const fetchRegions = async () => {
      const res = await getAllRegions();
      const data = await res;
      setAvailableRegions(data.body.regions);
    };
    fetchTags();
    fetchRegions();
  }, []);

  const handleImageChange = async (files: FileList | null) => {
    if (!files) return;
    const compressedBlob = await imageCompression(files[0], {
      maxSizeMB: 0.8,
      maxWidthOrHeight: 800,
      useWebWorker: true,
    });
    const compressedFile = new File([compressedBlob], files[0].name, { type: compressedBlob.type });
    setFormState((prev) => ({ ...prev, main_image: compressedFile }));
  };

  const handleTagSelection = (tagId: number) => {
    setFormState((prev) => ({
      ...prev,
      tags: prev.tags.includes(tagId) ? prev.tags.filter((id) => id !== tagId) : [...prev.tags, tagId],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('title', formState.spaceName);
      formData.append('description', formState.description);
      formData.append('location', formState.location);
      formData.append('price_per_day', formState.pricePerDay.toString());
      formData.append('max_duration_days', formState.maxDurationDays.toString());

      if (formState.main_image instanceof File) {
        formData.append('main_image', formState.main_image);
      }

      if (formState.regionId !== null) {
        formData.append('regionId', formState.regionId.toString());
      }

      formState.tags.forEach((tagId) => formData.append('tags[]', tagId.toString()));

      if (initialData?.id) {
        // Edit 모드
        await updateSpaceData(Number(initialData.id), formData);
        alert('공간 수정 완료!');
      } else {
        // Upload 모드
        await uploadSpace(formData);
        alert('공간 등록 완료!');
      }

      onSubmitSuccess?.();
    } catch (err) {
      console.error(err);
      alert('오류 발생');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <input
        type="text"
        placeholder="공간 이름"
        value={formState.spaceName}
        onChange={(e) => setFormState({ ...formState, spaceName: e.target.value })}
        className="w-full border px-4 py-2 rounded"
        required
      />
      <textarea
        placeholder="설명"
        value={formState.description}
        onChange={(e) => setFormState({ ...formState, description: e.target.value })}
        className="w-full border px-4 py-2 rounded h-32"
        required
      />
      <select
        value={formState.regionId ?? ''}
        onChange={(e) =>
          setFormState({ ...formState, regionId: e.target.value ? parseInt(e.target.value) : null })
        }
        className="w-full border px-4 py-2 rounded"
        required
      >
        <option value="">지역 선택</option>
        {availableRegions.map((region) => (
          <option key={region.id} value={region.id}>
            {region.name}
          </option>
        ))}
      </select>
      <input
        type="text"
        placeholder="상세 주소"
        value={formState.location}
        onChange={(e) => setFormState({ ...formState, location: e.target.value })}
        className="w-full border px-4 py-2 rounded"
        required
      />
      <input type="number" value={formState.pricePerDay} onChange={(e) => setFormState({ ...formState, pricePerDay: parseInt(e.target.value) })} className="w-full border px-4 py-2 rounded" />
      <input type="number" value={formState.maxDurationDays} onChange={(e) => setFormState({ ...formState, maxDurationDays: parseInt(e.target.value) })} className="w-full border px-4 py-2 rounded" />
      <input type="file" onChange={(e) => handleImageChange(e.target.files)} className="w-full border px-4 py-2 rounded" />
      <div className="flex flex-wrap gap-2">
        {availableTags.map((tag) => (
          <button
            key={tag.id}
            type="button"
            onClick={() => handleTagSelection(tag.id)}
            className={`px-3 py-1 rounded text-sm ${
              formState.tags.includes(tag.id) ? 'bg-blue-500 text-white' : 'bg-gray-200 text-black'
            }`}
          >
            {tag.name}
          </button>
        ))}
      </div>
      <button type="submit" className="w-full bg-blue-500 text-white py-2 rounded">
        {initialData?.id ? '수정하기' : '공간업로드'}
      </button>
    </form>
  );
};

export default SpaceForm;

export {};
